import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GBP, ukDateLong, ymd } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { EmailLinkForm } from "@/components/ClientAccount";
import { CalendarDays, LogOut } from "lucide-react";
import { toast } from "sonner";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { Logo } from "@/pages/Landing";

function Booking({ b, onCancel }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancel your ${b.service_name} on ${ukDateLong(b.date)} at ${b.start}?${b.policy_text ? `\n\nCancellation policy: ${b.policy_text}` : ""}`)) return;
    setBusy(true);
    try { await api.post(`/public/client/bookings/${b.id}/cancel`); toast.success("Booking cancelled"); onCancel(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`account-booking-${b.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex items-start gap-3">
      <CalendarDays className="h-5 w-5 text-[#B07E4F] mt-0.5" />
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold">{b.service_name}</div>
        <div className="text-stone-600">{ukDateLong(b.date)} at {b.start} · {b.business_name}</div>
        <div className="text-xs text-stone-500 mt-0.5 capitalize">{b.status.replace("_", " ")}{b.deposit > 0 && ` · deposit ${GBP(b.deposit)} ${b.deposit_paid ? "paid" : "unpaid"}`}</div>
        {b.inside_notice && <p data-testid={`account-booking-notice-${b.id}`} className="text-xs text-amber-800 mt-1.5">Within the {b.notice_hours}-hour cancellation notice. To change it, call {b.business_phone || "the salon"}.</p>}
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        {b.business_slug && <Link to={`/${b.business_slug}`} className="text-xs underline">Book again</Link>}
        {b.can_cancel && <button data-testid={`account-cancel-${b.id}`} disabled={busy} onClick={cancel} className="text-xs text-rose-700 underline">{busy ? "Cancelling…" : "Cancel"}</button>}
      </div>
    </div>
  );
}

export default function Account() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [del, setDel] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => { const { data } = await api.get("/public/client/me"); setD(data); }, []);

  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { load(); return; }
    api.post("/public/client/verify", { token })
      .then(() => { const next = params.get("next"); if (next && next !== "/account" && next.startsWith("/")) navigate(next, { replace: true }); else { navigate("/account", { replace: true }); load(); } })
      .catch((e) => { setError(formatApiError(e.response?.data?.detail)); load(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => { await api.post("/public/client/logout"); load(); };
  const today = ymd(new Date());
  const upcoming = (d?.bookings || []).filter((b) => b.date >= today).reverse();
  const past = (d?.bookings || []).filter((b) => b.date < today);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="max-w-xl mx-auto px-5 py-5 flex items-center justify-between"><Link to="/"><Logo /></Link>
        {d?.signed_in && <Button variant="ghost" size="sm" data-testid="account-logout" onClick={logout} className="rounded-full"><LogOut className="h-4 w-4 mr-1" />Sign out</Button>}</header>
      <main className="max-w-xl mx-auto px-5 pb-16">
        {error && <p data-testid="account-error" className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{error}</p>}
        {!d ? <p className="text-sm text-stone-500">Loading…</p> : !d.signed_in ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="account-signin">
            <h1 className="font-extrabold text-2xl mb-4">Your bookings</h1>
            <EmailLinkForm nextPath="/account" testid="account-signin" />
          </div>
        ) : (
          <div className="space-y-6" data-testid="account-page">
            <div><h1 className="font-extrabold text-2xl">Hi {d.profile.first_name || "there"}</h1><p className="text-sm text-stone-600">{d.profile.email} · {d.profile.mobile}</p></div>
            <div><h2 className="font-semibold mb-2">Upcoming</h2><div className="space-y-2">{upcoming.map((b) => <Booking key={b.id} b={b} onCancel={load} />)}{upcoming.length === 0 && <p className="text-sm text-stone-500">No upcoming bookings. <Link to="/" className="underline">Find a salon</Link></p>}</div></div>
            {past.length > 0 && <div><h2 className="font-semibold mb-2">Past</h2><div className="space-y-2">{past.slice(0, 20).map((b) => <Booking key={b.id} b={b} />)}</div></div>}
            <div className="pt-4 border-t border-stone-200">
              <button data-testid="client-delete-account" onClick={() => setDel(true)} className="text-sm text-rose-700 underline">Delete my account</button>
              <p className="text-xs text-stone-500 mt-1">Removes your saved details and sign-in. Your bookings stay with the salon.</p>
            </div>
            {del && <DeleteAccountDialog title="Delete your account?" confirmName="DELETE"
              warning="Your saved details and sign-in will be removed. Upcoming bookings are not cancelled, and salons keep their own booking records."
              onClose={() => setDel(false)}
              onConfirm={async () => {
                try { await api.post("/public/client/me/delete"); }
                catch (e) { throw new Error(formatApiError(e.response?.data?.detail)); }
                toast.success("Your account has been deleted"); setDel(false); load();
              }} />}
          </div>
        )}
      </main>
    </div>
  );
}
